/**
 * FeatureCard - Multi-purpose feature component.
 *
 * Variants:
 *   Default      – icon + title + description card (single feature)
 *   Horizontal   – icon left, text right
 *   Minimal      – text-only, no border
 *   MiniGrid     – renders a responsive grid of feature items from `items` prop
 *   DetailPanel  – rich detail panel (title + body + bullet list), shown/hidden
 *                  by TabSelectorCard via a shared tab-change custom event.
 *                  Use `panelKey` (not React's reserved `key`) to identify panel.
 */
import React, { useState, useEffect } from 'react';

interface FeatureItem { title?: string; body?: string; description?: string; icon?: string; }

export interface FeatureCardProps {
  variant?: 'Default' | 'Horizontal' | 'Minimal' | 'MiniGrid' | 'DetailPanel';
  // Single-feature props
  title?: string;
  description?: string;
  body?: string;
  href?: string;
  icon?: React.ReactNode;
  // MiniGrid props
  columns?: number;
  items?: FeatureItem[];
  // DetailPanel props — note: use panelKey, NOT React's reserved 'key'
  panelKey?: string;
  bullets?: string[];
  className?: string;
}

const DEFAULT_ICON = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2.5" aria-hidden="true" style={{ flexShrink: 0, marginTop: '2px' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const FeatureCard: React.FC<FeatureCardProps> = (props) => {
  const {
    variant = 'Default',
    title,
    description,
    body,
    href,
    icon,
    columns = 3,
    items = [],
    panelKey,
    bullets = [],
    className = '',
  } = props;

  const bodyText = body || description || '';

  // ── DetailPanel: controlled visibility via TabSelectorCard event bus ──
  const [visible, setVisible] = useState<boolean>(!panelKey);
  useEffect(() => {
    if (!panelKey) return;
    const handler = (e: Event) => {
      setVisible((e as CustomEvent<string>).detail === panelKey);
    };
    window.addEventListener('wg-tab-change', handler as EventListener);
    return () => window.removeEventListener('wg-tab-change', handler as EventListener);
  }, [panelKey]);

  if (panelKey && !visible) return null;

  // ── MiniGrid: grid of simple feature items ──
  if (variant === 'MiniGrid' || (items.length > 0 && !panelKey)) {
    return (
      <div
        className={`feature-mini-grid ${className}`}
        style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 'var(--spacing-6)', marginTop: 'var(--spacing-6)' }}
      >
        {items.map((item, i) => (
          <div key={i} className="feature-card" style={{ padding: 'var(--spacing-6)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface-raised)' }}>
            <h3 style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--spacing-2)', color: 'var(--color-text)' }}>
              {item.title}
            </h3>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', lineHeight: 'var(--line-height-relaxed)', margin: 0 }}>
              {item.body || item.description}
            </p>
          </div>
        ))}
      </div>
    );
  }

  // ── DetailPanel: rich expandable panel ──
  if (variant === 'DetailPanel' || (bullets.length > 0)) {
    return (
      <div
        className={`feature-detail-panel ${className}`}
        style={{ padding: 'var(--spacing-8)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface-raised)' }}
        role="region"
        aria-label={title}
      >
        {title && (
          <h2 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', marginBottom: 'var(--spacing-4)', color: 'var(--color-text)' }}>
            {title}
          </h2>
        )}
        {bodyText && (
          <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-text-muted)', lineHeight: 'var(--line-height-relaxed)', marginBottom: bullets.length > 0 ? 'var(--spacing-6)' : 0 }}>
            {bodyText}
          </p>
        )}
        {bullets.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--spacing-3)' }}>
            {bullets.map((bullet, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text)' }}>
                <CheckIcon />
                {bullet}
              </li>
            ))}
          </ul>
        )}
        {href && (
          <a href={href} className="btn btn-primary" style={{ marginTop: 'var(--spacing-6)', display: 'inline-flex' }}>
            Learn more
          </a>
        )}
      </div>
    );
  }

  // ── Horizontal variant ──
  if (variant === 'Horizontal') {
    return (
      <div className={`feature-card feature-horizontal ${className}`} style={{ display: 'flex', gap: 'var(--spacing-5)', alignItems: 'flex-start' }} role="article">
        <div className="icon-box icon-box-md" style={{ flexShrink: 0 }} aria-hidden="true">{icon ?? DEFAULT_ICON}</div>
        <div>
          <h3 style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-base)', marginBottom: 'var(--spacing-2)', color: 'var(--color-text)' }}>{title}</h3>
          <p style={{ color: 'var(--color-text-muted)', lineHeight: 'var(--line-height-relaxed)', fontSize: 'var(--font-size-sm)', margin: 0 }}>{bodyText}</p>
        </div>
      </div>
    );
  }

  // ── Default variant ──
  const content = (
    <>
      <div className="icon-box icon-box-lg" style={{ marginBottom: 'var(--spacing-5)' }} aria-hidden="true">{icon ?? DEFAULT_ICON}</div>
      <h3 style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-lg)', marginBottom: 'var(--spacing-3)', color: 'var(--color-text)' }}>{title}</h3>
      <p style={{ color: 'var(--color-text-muted)', lineHeight: 'var(--line-height-relaxed)', fontSize: 'var(--font-size-sm)', margin: 0 }}>{bodyText}</p>
      {href && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-1)', color: 'var(--color-primary)', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-medium)', marginTop: 'var(--spacing-4)' }}>
          Learn more
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </span>
      )}
    </>
  );

  if (href) {
    return <a href={href} className={`feature-card ${className}`} style={{ textDecoration: 'none', display: 'block' }} aria-label={title}>{content}</a>;
  }
  return <div className={`feature-card ${className}`} role="article">{content}</div>;
};

export default FeatureCard;
